/**
 * Shelf picker overlaid on a book cover.
 *
 * A three-dot chip opens the shelf choices ("want to read", "already read",
 * "not for me") and the chosen one is echoed back as a pill along the bottom of
 * the cover. Replaces the earlier thumbs up/down, which conflated "bad
 * recommendation" with "I already own this".
 *
 * The menu is sized to the card rather than to the chip: the carousel row is an
 * `overflow-x-auto` scroller, so anything wider than the card would be clipped.
 */

import { Bookmark, Check, MoreVertical, X } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BookShelf } from '../services/api';

/** Marks the shelf control the onboarding tour points at (step 2). */
export const SHELF_MENU_TOUR_ATTR = 'data-tour-shelf-menu';

/**
 * Lets the tour hold the menu open so the shopper can read the choices, without
 * simulating a click: a real click would pull focus into the menu and leave its
 * buttons operable from the keyboard while the tour is meant to be modal.
 * Dispatched on the element carrying `SHELF_MENU_TOUR_ATTR`, detail = open.
 */
export const SHELF_MENU_PREVIEW_EVENT = 'indigo:shelf-menu-preview';

const SHELF_OPTIONS: Array<{ shelf: BookShelf; icon: typeof Bookmark; labelKey: string }> = [
  { shelf: 'want_to_read', icon: Bookmark, labelKey: 'shelf.wantToRead' },
  { shelf: 'already_read', icon: Check, labelKey: 'shelf.alreadyRead' },
  { shelf: 'not_for_me', icon: X, labelKey: 'shelf.notForMe' },
];

const shelfLabelKey = (shelf: BookShelf): string =>
  SHELF_OPTIONS.find((option) => option.shelf === shelf)?.labelKey ?? 'shelf.wantToRead';

interface BookShelfMenuProps {
  shelf: BookShelf | null;
  onChange: (shelf: BookShelf | null) => void;
  /** Tag this instance so the onboarding tour can find and spotlight it. */
  isTourAnchor?: boolean;
}

const BookShelfMenu: React.FC<BookShelfMenuProps> = ({ shelf, onChange, isTourAnchor = false }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback((returnFocus: boolean) => {
    setIsOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Dismiss on outside pointer or Escape. Escape returns focus to the chip;
  // an outside click leaves focus wherever the shopper put it.
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close]);

  // Move focus into the menu on open so keyboard users land on the first choice.
  // Only on a real open: the tour's preview is for reading, not for using.
  useEffect(() => {
    if (!isOpen) return;
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [isOpen]);

  useEffect(() => {
    const node = containerRef.current;
    if (!isTourAnchor || !node) return;
    const handlePreview = (event: Event) => setIsPreviewing((event as CustomEvent<boolean>).detail);
    node.addEventListener(SHELF_MENU_PREVIEW_EVENT, handlePreview);
    return () => node.removeEventListener(SHELF_MENU_PREVIEW_EVENT, handlePreview);
  }, [isTourAnchor]);

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items: HTMLButtonElement[] = [];
    menuRef.current?.querySelectorAll<HTMLButtonElement>('button').forEach((item) => {
      items.push(item);
    });
    if (items.length === 0) return;
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + offset + items.length) % items.length;
    items[nextIndex].focus();
  };

  const handleSelect = (selected: BookShelf) => {
    // Re-picking the current shelf clears it, matching the toggle behaviour
    // shoppers already know from the rest of the card.
    onChange(shelf === selected ? null : selected);
    close(true);
  };

  // A preview is the tour showing the choices, not the shopper opening them, so
  // it stays out of the tab order and out of the accessibility tree; the tour
  // tooltip describes the same thing in words.
  const isPreviewOnly = isPreviewing && !isOpen;
  const tourAttrs = isTourAnchor ? { [SHELF_MENU_TOUR_ATTR]: 'true' } : {};

  return (
    /* Anchored to the bottom of the cover so the menu grows upward over the
       artwork instead of being clipped by the scroller. Pointer events are
       re-enabled per child, so the cover around the chip still opens the book. */
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-x-1 bottom-1 z-20"
      // The card itself opens book details on click; shelf actions must not.
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      role="presentation"
      {...tourAttrs}
    >
      {(isOpen || isPreviewing) && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={t('shelf.menuLabel')}
          aria-hidden={isPreviewOnly}
          onKeyDown={handleMenuKeyDown}
          className={`mb-1 w-full overflow-hidden rounded-sm border border-indigo-brand-border bg-white shadow-md ${
            isPreviewOnly ? 'pointer-events-none' : 'pointer-events-auto'
          }`}
        >
          {SHELF_OPTIONS.map(({ shelf: option, icon: Icon, labelKey }) => {
            const isSelected = shelf === option;
            return (
              <button
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={isSelected}
                tabIndex={isPreviewOnly ? -1 : undefined}
                onClick={() => handleSelect(option)}
                className={`flex w-full items-center gap-1.5 border-b border-indigo-brand-border px-2 py-2 text-left text-[11px] leading-tight last:border-b-0 ${
                  isSelected
                    ? 'bg-indigo-brand-black text-white'
                    : 'text-indigo-brand-text hover:bg-indigo-brand-off-white'
                }`}
              >
                <Icon size={12} className="flex-shrink-0" />
                <span className="truncate">{t(labelKey)}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-1">
        {shelf ? (
          <span className="inline-flex min-w-0 items-center gap-1 rounded-sm bg-indigo-brand-black px-1.5 py-1 text-[11px] font-medium leading-none text-white">
            <Check size={11} className="flex-shrink-0" />
            <span className="truncate">{t(shelfLabelKey(shelf))}</span>
          </span>
        ) : (
          <span aria-hidden="true" />
        )}

        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label={t('shelf.menuLabel')}
          title={t('shelf.menuLabel')}
          // Visible chip is 28px; the padded hit area clears the 44px minimum.
          className="pointer-events-auto flex h-11 w-11 flex-shrink-0 items-center justify-center text-indigo-brand-text"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full border border-indigo-brand-border bg-white/95 shadow-sm transition-colors hover:bg-indigo-brand-off-white">
            <MoreVertical size={15} />
          </span>
        </button>
      </div>
    </div>
  );
};

export default BookShelfMenu;
